import { useNavigation } from '@react-navigation/native';
import * as Clipboard from 'expo-clipboard';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, Platform, Share } from 'react-native';
import Purchases from 'react-native-purchases';

import { Alert } from '@components/Alert';
import {
  useCreateContactInvitationUrlMutation,
  useGetMeQuery,
  User,
  useUserAvatarsByIdsLazyQuery,
} from '@graphql/generated';
import { useCrashlytics } from '@hooks/useCrashlytics';
import { isUrlExpired } from '@hooks/useGetDocumentsByClientIdsQuery';
import { useRevalidateSessionOnFocus } from '@hooks/useRevalidateSessionOnFocus';
import {
  AvatarCachePrefix,
  AvatarThumbnailCachePrefix,
} from '@src/constants/constants';
import { useAuthContext } from '@src/context/authContext';
import { normalizePhoneNumbers } from '@utils/formatters/phone';
import {
  logError,
  LogGraphError,
  LogNetworkError,
} from '@utils/logNonFatalError';
import { AsyncStorage } from '@utils/storage';

type InvitePhoneInput = User['phoneNumber'] | string[];

type MeContext = {
  me?: User;
  refetchMe: () => Promise<void>;
  inviteViaPhone: (
    phoneNumber: InvitePhoneInput,
    showInviteeAlert?: User['phoneNumber' | 'name']
  ) => void;
  shareInviteLink: () => void;
  copyInviteLinkToClipboard: () => void;
  isPaid?: boolean;
  setPaid: React.Dispatch<React.SetStateAction<boolean>>;
  loading?: boolean;
  meNetworkReady: boolean;
};

export const meContext = createContext<MeContext | undefined>(undefined);

export const MeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigation = useNavigation();
  const { setAttributes, setUserId } = useCrashlytics();
  const [isPaid, setPaid] = useState<boolean>(false);
  const [meNetworkReady, setMeNetworkReady] = useState(false);
  const invitePhoneNumbersRef = useRef<string[]>([]);

  const { Provider } = meContext;
  const { t } = useTranslation('models');
  const { t: tShared } = useTranslation('shared');
  const { token } = useAuthContext();

  const [getUserAvatarsByIds] = useUserAvatarsByIdsLazyQuery();

  const [avatarUrl, setAvatarUrl] = useState('');

  const { data, loading, error, refetch } = useGetMeQuery({
    skip: !token,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!error) return;
    if (error.networkError) {
      const event: LogNetworkError = {
        message: 'Get Me Query Network Failure',
        errorMessage: error.message,
        statusCode:
          'statusCode' in error.networkError
            ? error.networkError.statusCode
            : -1,
      };
      logError(event);
    }
    error.graphQLErrors.forEach((graphError) => {
      const event: LogGraphError = {
        message: 'Get Me Query GraphQL Error',
        errorMessage: graphError.message,
        operationName: 'GetMe',
      };
      logError(event);
    });
  }, [error]);

  const refetchMe = async () => {
    try {
      await refetch();
    } catch (error) {
      console.error('Error refetching me data:', error);
    }
  };

  // TA-2593 (VAPT): re-verify the session with the server whenever a web tab
  // regains focus, so a token revoked elsewhere (e.g. by a password reset)
  // stops being usable instead of the tab living on off the persisted cache.
  // getMe is the app's existing "who am I" query, so this reuses a request the
  // session already depends on rather than adding a new auth probe.
  useRevalidateSessionOnFocus({ hasToken: !!token, revalidate: refetchMe });

  // With notifyOnNetworkStatusChange: true, loading cycles true→false during the
  // background network request even when cache data is already available.
  // Tracking this transition lets consumers know a server-confirmed result has arrived,
  // preventing stale persisted-cache values from being treated as authoritative.
  const prevLoadingRef = useRef(loading);
  useEffect(() => {
    if (!loading && prevLoadingRef.current && !error) {
      setMeNetworkReady(true);
    }
    prevLoadingRef.current = loading;
  }, [loading, error]);

  const { getMe } = data || {
    getMe: undefined,
  };

  const getAvatarUrl = (avatarId: string, xurl: string | undefined) => {
    const savedId = AvatarCachePrefix + avatarId;
    const savedThumbId = AvatarThumbnailCachePrefix + avatarId;
    // TA-10914: both callers (no cached URL, or a cached URL that
    // `isUrlExpired` already confirmed is stale) need a genuinely fresh
    // signature. Apollo's default cache-first policy could otherwise hand
    // back the same stale `userAvatarsByIds` result for these identical
    // variables and have it re-persisted as if it were new (see the
    // matching fix in CachedImage.tsx).
    getUserAvatarsByIds({
      variables: {
        ids: [avatarId],
      },
      fetchPolicy: 'network-only',
    }).then((rtn) => {
      const urls = rtn.data?.userAvatarsByIds;
      if (urls && urls.length > 0) {
        setAvatarUrl(urls[0].url);
        AsyncStorage.setItem(savedId, urls[0].url);
        if (urls[0].thumbnail) {
          AsyncStorage.setItem(savedThumbId, urls[0].thumbnail);
        }
      } else if (xurl) {
        setAvatarUrl(xurl);
      }
    });
  };
  useEffect(() => {
    if (getMe?.avatarId) {
      const savedId = getMe.avatarId;

      AsyncStorage.getItem(AvatarCachePrefix + savedId).then((xurl) => {
        if (xurl) {
          if (isUrlExpired(xurl, new Date())) {
            getAvatarUrl(savedId, xurl);
          } else {
            setAvatarUrl(xurl);
          }
        } else {
          getAvatarUrl(savedId, undefined);
        }
      });
    }
  }, [getMe]);

  const memoizedMe = useMemo(() => {
    if (getMe?.avatar) {
      return { ...getMe, avatar: { ...getMe?.avatar, url: avatarUrl } };
    } else {
      return getMe;
    }
  }, [
    getMe?.id,
    getMe?.name,
    getMe?.calendarUrl,
    getMe?.phoneNumber,
    getMe?.skills,
    avatarUrl,
    getMe?.contactsSyncedAt,
    getMe?.email,
    getMe?.timeZone,
    getMe?.tutoralizationSetting,
    getMe?.primaryTeamId,
    getMe?.notificationSetting,
    getMe?.teams,
    getMe?.personalProjectsCount,
    getMe?.completedWebTutorialFlows,
    getMe?.onboardingFlow,
    getMe?.hasSeenTagTooltipNative,
    getMe?.hasSeenTagTooltipWeb,
    getMe?.onboardingState,
    getMe?.onboardingComplete,
  ]);

  const { inviteUrl } = memoizedMe || {
    inviteUrl: '',
  };

  const copyInviteLinkToClipboard = useCallback(async () => {
    await Clipboard.setStringAsync(inviteUrl);
  }, [inviteUrl]);

  const showInviteError = useCallback(
    (message?: string) => {
      Alert.alert(
        t('invite.title'),
        message || tShared('updateFailedTryAgain')
      );
    },
    [t, tShared]
  );

  const [createContactInvitationUrl, { data: contactInviteData }] =
    useCreateContactInvitationUrlMutation();

  useEffect(() => {
    if (!contactInviteData) return;
    const invitationUrl = contactInviteData.createContactInvitationUrl;
    const numbers = invitePhoneNumbersRef.current;

    if (!invitationUrl || numbers.length === 0) {
      showInviteError('Unable to prepare the invite. Please try again.');
      return;
    }

    const message = `${t('invite.message')} ${invitationUrl}`;
    const encodedMessage = encodeURIComponent(message);
    const url = Platform.select({
      ios: `sms:/open?addresses=${numbers.join(',')}&body=${encodedMessage}`,
      android: `sms:${numbers[0]}?body=${encodedMessage}`,
    });

    if (!url) {
      console.error('Failed to construct SMS URL.');
      showInviteError('Unable to open the SMS app on this device.');
      return;
    }

    const openSmsApp = async () => {
      try {
        await Linking.openURL(url);
        if (numbers.length > 1) {
          navigation.goBack();
        }
      } catch (err) {
        console.error('Error opening SMS app:', err);
        showInviteError('Unable to open the SMS app. Please try again.');
      }
    };

    openSmsApp();
  }, [contactInviteData, navigation, showInviteError, t]);

  const inviteViaPhone = useCallback(
    (
      phoneNumber: InvitePhoneInput,
      showInviteeAlert?: User['phoneNumber' | 'name']
    ) => {
      const normalizedNumbers = normalizePhoneNumbers(phoneNumber);

      if (normalizedNumbers.length === 0) {
        showInviteError('This contact does not have a valid phone number.');
        return;
      }

      const openMessages = () => {
        invitePhoneNumbersRef.current = normalizedNumbers;
        createContactInvitationUrl({
          skip: !token,
          variables: {
            attributes: {
              phoneNumber: normalizedNumbers,
            },
          },
          onError: (error) => {
            console.error('Failed to create contact invitation URL:', error);
            showInviteError('Unable to create the invite. Please try again.');
          },
        });
      };

      if (showInviteeAlert) {
        Alert.alert(
          t('invite.title'),
          t('invite.alert', { user: showInviteeAlert }),
          [
            { text: 'Cancel' },
            {
              text: 'Invite',

              onPress: () => {
                openMessages();
              },
            },
          ]
        );
      } else {
        openMessages();
      }
    },
    [createContactInvitationUrl, showInviteError, t, token]
  );

  const shareInviteLink = useCallback(async () => {
    if (inviteUrl) {
      try {
        await Share.share({
          message: inviteUrl,
        });
      } catch (err) {
        alert(err);
      }
    }
  }, [inviteUrl]);

  useEffect(() => {
    if (!memoizedMe) return;

    const sendCrashlytics = async () => {
      setUserId && (await setUserId(memoizedMe.id));
      setAttributes &&
        (await setAttributes({
          email: memoizedMe.email ?? '',
        }));
    };

    const sendRevenueCat = async () => {
      if (Platform.OS === 'web') return;
      // Purchases
      const { customerInfo, created } = await Purchases.logIn(memoizedMe.email);
      console.log({ customerInfo, created });
    };

    sendCrashlytics();
    sendRevenueCat();
  }, [memoizedMe]);

  const providerValue = useMemo(() => {
    return {
      me: memoizedMe as User,
      refetchMe,
      inviteViaPhone,
      shareInviteLink,
      copyInviteLinkToClipboard,
      isPaid,
      setPaid,
      loading,
      meNetworkReady,
    };
  }, [
    copyInviteLinkToClipboard,
    inviteViaPhone,
    isPaid,
    loading,
    memoizedMe,
    shareInviteLink,
    meNetworkReady,
  ]);

  return <Provider value={providerValue}>{children}</Provider>;
};

const useMe = (): MeContext => {
  const context = useContext(meContext);
  if (context === undefined) {
    throw new Error('useMe must be used within a Provider');
  }
  return context;
};

export default useMe;
